import {
  InteractionRequiredAuthError,
  InteractionType,
} from '@azure/msal-browser'
import {
  useAccount,
  useIsAuthenticated,
  useMsal,
  useMsalAuthentication,
} from '@azure/msal-react'
import {
  type ComponentType,
  type PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { clearSession, logoutAuthProfile } from '@app-store'
import type { EcrmMsalAuthRootState } from './types'
import { buildMsalRedirectRequest } from './msalRedirectRequest'
import { clearMsalAppSessionOnly } from './msalClient'
import {
  clearMsalAccessTokenBridge,
  consumeMsalAccessTokenFromRedirect,
} from './msalRedirectBridge'

export interface MsalAuthGuardProps extends PropsWithChildren {
  /** Scopes for redirect + silent acquisition (memoize in the app for stable identity). */
  scopes: string[]
  msalInstance: import('@azure/msal-browser').PublicClientApplication
  LoginAttemptComponent: ComponentType<{
    token?: string | null
    text: string
    idToken?: string | null
    refreshToken?: string | null
  }>
}

/**
 * MSAL Entra guard: redirect-based `useMsalAuthentication`, silent token with redirect fallback
 * (aligned with agent-desk). Requires `MsalProvider` at app root and shared `msalInstance`.
 */
export function MsalAuthGuard({
  children,
  scopes,
  msalInstance,
  LoginAttemptComponent,
}: MsalAuthGuardProps) {
  const dispatch = useDispatch()
  const forceMsalPromptNextLogin = useSelector(
    (s: EcrmMsalAuthRootState) => !!s.session.forceMsalPromptNextLogin
  )
  const redirectRequest = useMemo(
    () => buildMsalRedirectRequest(forceMsalPromptNextLogin, scopes),
    [forceMsalPromptNextLogin, scopes]
  )

  const { error } = useMsalAuthentication(
    InteractionType.Redirect,
    redirectRequest
  )
  const { accounts, instance } = useMsal()
  const account = useAccount(accounts[0] || null)
  const isMsalAuthenticated = useIsAuthenticated()
  const isAppAuthenticated = useSelector(
    (s: EcrmMsalAuthRootState) => s.session.isAuthenticated
  )
  const [accessToken, setAccessToken] = useState<string | null>(null)

  useEffect(() => {
    if (!account) return

    const fromRedirect = consumeMsalAccessTokenFromRedirect()
    if (fromRedirect) {
      setAccessToken(fromRedirect)
      return
    }

    if (accessToken) return

    const silentRequest = { scopes, account }

    void instance
      .acquireTokenSilent(silentRequest)
      .then((response) => {
        setAccessToken(response.accessToken)
      })
      .catch(async (err) => {
        if (err instanceof InteractionRequiredAuthError) {
          const request = {
            ...buildMsalRedirectRequest(forceMsalPromptNextLogin, scopes),
            account,
          }
          void instance.acquireTokenRedirect(request)
          return
        }
        console.error('MSAL acquireTokenSilent failed:', err)
        await clearMsalAppSessionOnly(msalInstance)
        clearMsalAccessTokenBridge()
        dispatch(logoutAuthProfile())
        dispatch(clearSession())
        window.location.assign(window.location.origin + '/logout')
      })
  }, [
    account,
    instance,
    accessToken,
    dispatch,
    forceMsalPromptNextLogin,
    scopes,
    msalInstance,
  ])

  useEffect(() => {
    if (!error) return
    console.error('MSAL Authentication Error:', error)
    void (async () => {
      await clearMsalAppSessionOnly(msalInstance)
      clearMsalAccessTokenBridge()
      dispatch(logoutAuthProfile())
      dispatch(clearSession())
      window.location.assign(window.location.origin + '/logout')
    })()
  }, [error, dispatch, msalInstance])

  if (isMsalAuthenticated && isAppAuthenticated) {
    return children
  } else if (isMsalAuthenticated && !isAppAuthenticated) {
    return (
      <LoginAttemptComponent token={accessToken} text={'Authenticating...'} />
    )
  } else {
    return null
  }
}
